/**
 * Chat Time Context
 *
 * Provides a shared timer for chat message relative timestamps.
 * Uses bucket-based values to minimize re-renders - only timestamp spans
 * re-render, not entire message rows.
 *
 * Issue #769 - Performance: Remove forceUpdate anti-pattern in ClubChatPage
 *
 * IMPORTANT: Wrap at Chat page boundary, NOT App root.
 * This ensures the interval only runs when chat is mounted.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// 10-second buckets for recent messages
const BUCKET_INTERVAL_MS = 10_000;

interface ChatTimeContextValue {
  /** Current time bucket - changes every 10 seconds */
  bucket: number;
}

const ChatTimeContext = createContext<ChatTimeContextValue | undefined>(undefined);

interface ChatTimeProviderProps {
  children: ReactNode;
}

/**
 * Provider that ticks every 10 seconds with a bucket value.
 * Components subscribing via useChatTimeBucket will re-render on each tick.
 *
 * Uses single shared interval (not per-message timers) to avoid memory leaks
 * when infinite scroll accumulates hundreds of messages.
 */
export function ChatTimeProvider({ children }: ChatTimeProviderProps) {
  const [bucket, setBucket] = useState(() => Math.floor(Date.now() / BUCKET_INTERVAL_MS));

  useEffect(() => {
    const interval = setInterval(() => {
      setBucket(Math.floor(Date.now() / BUCKET_INTERVAL_MS));
    }, BUCKET_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return (
    <ChatTimeContext.Provider value={{ bucket }}>
      {children}
    </ChatTimeContext.Provider>
  );
}

/**
 * Hook to subscribe to chat time updates.
 * Only call this from components that need to re-render on time changes.
 *
 * Returns the current bucket value - use this as a dependency to trigger
 * recalculation of relative timestamps.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useChatTimeBucket(): number {
  const context = useContext(ChatTimeContext);

  // If used outside provider, return current bucket (no updates)
  // This allows components to work standalone during SSR or testing
  if (context === undefined) {
    // eslint-disable-next-line react-hooks/purity
    return Math.floor(Date.now() / BUCKET_INTERVAL_MS);
  }

  return context.bucket;
}
